import { Link, useLocation } from 'react-router-dom'
import { LOCALES, localeDef } from '@/i18n/locales'
import { useI18n } from '@/i18n/I18nProvider'
import { cn } from '@/lib/cn'

/**
 * A dropdown, but built from `<details>` and real `<a>` links — never a
 * `<select>` and never a JavaScript menu.
 *
 * Two constraints decide the mechanism. A crawler follows links and ignores a
 * select's options, and the whole reason every locale has its own URL is so the
 * six language versions of a page can be discovered and ranked separately; a
 * JavaScript switcher would make the `hreflang` block a promise nothing keeps.
 * And 117 of this site's documents — every imported digitopia.co page — ship
 * with no client bundle at all; they hide the switcher today only because they
 * have fewer than two alternates, and the day one of them gains a second, a menu
 * that needed React to open would be a button that does nothing. `<details>`
 * opens with the browser's own behaviour, and the links inside it are in the
 * HTML whether it is open or not.
 *
 * Switching stays on the same page rather than returning home — swapping the
 * locale segment and keeping the rest. That works for the *written* pages,
 * because every locale prerenders every route by construction (`localePath`).
 *
 * It does NOT work for the imported digitopia.co pages, and quietly claiming it
 * did was a bug worth naming: they exist only in the languages they were
 * imported in, and the Turkish ones carry Turkish slugs — `/tr/butce-donemi`, not
 * `/tr/budget-season`. Swapping the segment on `/en/industries` offered five
 * languages and 404'd in all five, on 124 pages. So a static page passes in the
 * alternates it actually has (`siblingsOf`, the same list its `hreflang` block
 * uses), and a language with no version of this page is simply not offered.
 */
export interface Alternate {
  locale: string
  href: string
}

export default function LanguageSwitcher({
  className,
  alternates,
  direction = 'down',
}: {
  className?: string
  alternates?: Alternate[]
  /**
   * Which way the panel opens. The footer copy sits at the very bottom of the
   * document, where a downward panel would hang off the end of the page.
   */
  direction?: 'down' | 'up'
}) {
  const { locale, t } = useI18n()
  const { pathname } = useLocation()

  // `/tr/some/page` → `some/page`. The first segment is always the locale.
  const rest = pathname.split('/').filter(Boolean).slice(1).join('/')

  // One language is not a choice. A switcher offering only the language you are
  // already reading is furniture.
  if (alternates && alternates.length < 2) return null

  const offered = alternates
    ? LOCALES.filter((l) => alternates.some((a) => a.locale === l.code))
    : LOCALES
  const hrefFor = (code: string) =>
    alternates
      ? (alternates.find((a) => a.locale === code)?.href as string)
      : rest
        ? `/${code}/${rest}`
        : `/${code}`

  const here = localeDef(locale)

  return (
    <nav aria-label={t.nav.languageLabel} className={cn('relative', className)}>
      <details className="group relative">
        <summary
          className={cn(
            'flex cursor-pointer list-none items-center gap-1.5 rounded-full px-2.5 py-1',
            'text-xs font-medium uppercase tracking-wide text-muted-foreground transition-colors',
            '[transition-duration:var(--transition-base)]',
            'hover:bg-muted hover:text-foreground group-open:bg-muted group-open:text-foreground',
            '[&::-webkit-details-marker]:hidden',
          )}
        >
          <span lang={here.code}>{here.code}</span>
          {/* Rotates on open, so the control says which state it is in. */}
          <svg
            viewBox="0 0 12 12"
            aria-hidden="true"
            className="h-2.5 w-2.5 transition-transform [transition-duration:var(--transition-base)] group-open:rotate-180"
          >
            <path
              d="M2.5 4.5 6 8l3.5-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </summary>

        <ul
          className={cn(
            'absolute right-0 z-50 min-w-[9.5rem] overflow-hidden rounded-[--radius]',
            'border border-border bg-card py-1 shadow-[var(--shadow-md)]',
            direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2',
          )}
        >
          {offered.map((l) => {
            const current = l.code === locale
            return (
              <li key={l.code}>
                <Link
                  to={hrefFor(l.code)}
                  hrefLang={l.code}
                  lang={l.code}
                  aria-current={current ? 'true' : undefined}
                  // Client-side navigation leaves the panel open, so close it.
                  // The imported pages never hydrate and never run this; they
                  // get a full page load, which closes it anyway.
                  onClick={(event) => {
                    event.currentTarget.closest('details')?.removeAttribute('open')
                  }}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                    '[transition-duration:var(--transition-base)]',
                    current
                      ? 'bg-primary/10 font-medium text-primary'
                      : 'text-foreground hover:bg-muted',
                  )}
                >
                  <span className="w-6 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                    {l.code}
                  </span>
                  {l.label}
                </Link>
              </li>
            )
          })}
        </ul>
      </details>
    </nav>
  )
}
